import { useEffect, useState } from 'react';
import { ChevronLeft, Loader2, CheckCircle2, AlertCircle, ShieldCheck, Ticket, X, ArrowRight } from 'lucide-react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '../../lib/stripe';
import {
  calculateCourtPrice, useCourt, useCreateCourtBooking, validatePromoCode,
  type PromoValidation,
} from '../../hooks/useAPI';
import supabase from '../../lib/supabase';
import { track } from '../../lib/analytics';

interface CourtCheckoutScreenProps {
  booking: {
    courtId: string;
    startAt: string;
    endAt: string;
    clubId: string;
    rentals?: Array<{ rentalItemId: string; quantity: number }>;
  };
  onNavigate: (screen: any, params?: any) => void;
  onConfirmed: () => void;
}

export default function CourtCheckoutScreen({ booking, onNavigate, onConfirmed }: CourtCheckoutScreenProps) {
  const { data: court } = useCourt(booking.courtId);
  const c = court as any;

  const [price, setPrice] = useState<any>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [bookingRef, setBookingRef] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Promo state. The booking is only created after the user either
  // applies a promo or clicks "continue without" — so we don't leave a
  // stale pending_payment row if they change their mind.
  const [promo, setPromo] = useState<PromoValidation | null>(null);
  const [promoReady, setPromoReady] = useState(false);

  const { createCourtBooking, isLoading } = useCreateCourtBooking();

  useEffect(() => {
    calculateCourtPrice(booking).then((p) => {
      setPrice(p);
      // Funnel start event — fires once per checkout entry, before the
      // user picks a promo. Properties match the canonical event
      // schema in docs/ANALYTICS.md.
      track('checkout_started', {
        kind: 'court',
        court_id: booking.courtId,
        club_id: p.clubId,
        amount: Number(p.total),
        currency: p.currency,
      });
    }).catch((e) => setError(e.message));
  }, [booking.courtId, booking.startAt, booking.endAt]);

  const init = async () => {
    setError(null);
    const { data, error } = await createCourtBooking({
      ...booking,
      promoCode: promo?.code,
    });
    if (error) {
      // create-court-booking returns 409 with a body like
      // `{ error: 'Slot not available', reason: 'Closed', conflicts: [...] }`.
      // The Supabase functions client surfaces the body as
      // (error as any).context.body — pull a richer message out of it
      // so the user knows why the slot is gone instead of seeing the
      // bare "Slot not available".
      let detail: string | null = null;
      try {
        const ctx: any = (error as any).context;
        const bodyText = typeof ctx?.body === 'string'
          ? ctx.body
          : await ctx?.json?.()?.catch?.(() => null);
        const parsed = bodyText && typeof bodyText === 'string' ? JSON.parse(bodyText) : bodyText;
        if (parsed?.reason) detail = String(parsed.reason);
      } catch { /* fall back to the raw message */ }
      const baseMessage = (error as any).message || 'Could not create booking';
      setError(detail ? `${baseMessage} — ${detail}` : baseMessage);
      return;
    }
    setBookingId(data?.booking?.id ?? null);
    setBookingRef(data?.booking?.bookingReference ?? null);
    setClientSecret(data?.payment?.clientSecret ?? null);
  };

  useEffect(() => {
    if (!clientSecret && price && promoReady) init();
  }, [price, promoReady]);

  if (success) {
    return (
      <div className="w-full h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 rounded-full bg-[#D4F04A] mx-auto mb-5 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-[#1A6B4F]" />
          </div>
          <h2 className="text-2xl mb-2">Court booked</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Your reference is <span className="font-medium text-foreground">{bookingRef}</span>.
            See you on court!
          </p>
          <button
            onClick={onConfirmed}
            className="h-11 px-6 rounded-xl bg-[#1A6B4F] text-white text-sm"
          >
            View my bookings
          </button>
        </div>
      </div>
    );
  }

  const start = new Date(booking.startAt);
  const end = new Date(booking.endAt);

  return (
    <div className="w-full min-h-screen bg-background pb-28">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => onNavigate('court', { id: booking.courtId })}
          className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-lg">Confirm & pay</h1>
          <p className="text-xs text-muted-foreground">Court booking</p>
        </div>
      </div>

      <div className="px-6 py-5 space-y-4">
        {c && (
          <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] rounded-2xl p-5 text-white">
            <div className="text-sm opacity-80">{c.clubs?.name}</div>
            <div className="text-lg font-medium mt-0.5">{c.name}</div>
            <div className="text-xs opacity-80 mt-1 capitalize">
              {c.surface} · {c.is_indoor ? 'Indoor' : 'Outdoor'}{c.has_lights ? ' · Lights' : ''}
            </div>
            <div className="h-px bg-white/15 my-3" />
            <div className="text-sm">
              {start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              {' · '}
              {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {' – '}
              {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        )}

        {price && (
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Court rental</span>
              <span>{price.currency} {Number(price.subtotal).toFixed(0)}</span>
            </div>
            {promo && (
              <div className="flex justify-between text-sm mt-1.5 text-[#1A6B4F]">
                <span className="inline-flex items-center gap-1">
                  <Ticket className="w-3.5 h-3.5" /> {promo.code}
                </span>
                <span>-{price.currency} {promo.discount.toFixed(0)}</span>
              </div>
            )}
            <div className="h-px bg-border my-2" />
            <div className="flex justify-between text-base">
              <span>Total</span>
              <span className="text-[#1A6B4F] font-medium">
                {price.currency}{' '}
                {(promo
                  ? Math.max(0, Number(price.total) - promo.discount)
                  : Number(price.total)
                ).toFixed(0)}
              </span>
            </div>
          </div>
        )}

        {price && !clientSecret && (
          <PromoCodeInput
            subtotal={Number(price.total)}
            applied={promo}
            onApply={(p) => {
              setPromo(p); setPromoReady(true);
              track('promo_applied', { kind: 'court', code: p.code, discount: p.discount });
            }}
            onRemove={() => {
              setPromo(null); setPromoReady(true);
              track('promo_removed', { kind: 'court' });
            }}
            onSkip={() => {
              setPromoReady(true);
              track('promo_skipped', { kind: 'court' });
            }}
            promoReady={promoReady}
          />
        )}

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3 text-sm text-destructive flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {clientSecret && bookingId ? (
          <Elements stripe={getStripe()} options={{ clientSecret }}>
            <CardPayment
              bookingId={bookingId}
              onSuccess={async () => {
                // Poll for webhook confirmation so the user sees the right status.
                for (let i = 0; i < 10; i++) {
                  const { data } = await supabase
                    .from('court_bookings')
                    .select('status')
                    .eq('id', bookingId)
                    .single();
                  if (data?.status === 'confirmed') break;
                  await new Promise((r) => setTimeout(r, 1000));
                }
                track('payment_confirmed', {
                  kind: 'court',
                  booking_id: bookingId,
                  booking_reference: bookingRef,
                  amount: price ? Number(price.total) : null,
                  currency: price?.currency ?? null,
                  promo_code: promo?.code ?? null,
                });
                setSuccess(true);
              }}
              onError={(msg) => {
                track('payment_failed', { kind: 'court', booking_id: bookingId, message: msg });
                setError(msg);
              }}
            />
          </Elements>
        ) : (isLoading || !price || promoReady) ? (
          // Only show the loader when the system is actually working —
          // either fetching the price, creating the booking, or waiting
          // on the PaymentIntent. When promoReady is false we're waiting
          // on the user, so the PromoCodeInput is the only thing to
          // show and a spinner here would look like a hang.
          <div className="py-6 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {isLoading || !price ? 'Preparing your booking…' : 'Loading payment…'}
          </div>
        ) : null}

        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="w-4 h-4 text-[#1A6B4F] flex-shrink-0 mt-0.5" />
          Payment is processed securely by Stripe. Your slot is held for 15 minutes while you pay.
        </div>
      </div>
    </div>
  );
}

function PromoCodeInput({
  subtotal, applied, onApply, onRemove, onSkip, promoReady,
}: {
  subtotal: number;
  applied: PromoValidation | null;
  onApply: (p: PromoValidation) => void;
  onRemove: () => void;
  onSkip: () => void;
  promoReady: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (applied) {
    return (
      <div className="bg-[#1A6B4F]/5 border border-[#1A6B4F]/20 rounded-2xl p-3 flex items-center justify-between text-sm">
        <div className="inline-flex items-center gap-2 text-[#1A6B4F]">
          <Ticket className="w-4 h-4" />
          <span className="font-mono font-medium">{applied.code}</span>
          <span className="text-xs text-[#1A6B4F]/70">applied</span>
        </div>
        <button
          onClick={onRemove}
          className="w-7 h-7 rounded-full hover:bg-[#1A6B4F]/10 flex items-center justify-center"
          aria-label="Remove promo"
        >
          <X className="w-4 h-4 text-[#1A6B4F]" />
        </button>
      </div>
    );
  }

  if (!open) {
    // Two states:
    //   - User hasn't decided yet (!promoReady) — primary "Continue to
    //     payment" CTA so they have an obvious path forward, with the
    //     promo entry as a secondary affordance.
    //   - User already continued (promoReady=true) — only show the
    //     promo entry; payment form / loader handles the rest.
    if (promoReady) {
      return (
        <button
          onClick={() => setOpen(true)}
          className="w-full h-11 rounded-xl border border-border bg-card text-sm inline-flex items-center justify-center gap-2 hover:bg-secondary"
        >
          <Ticket className="w-4 h-4 text-[#1A6B4F]" /> Have a promo code?
        </button>
      );
    }
    return (
      <div className="space-y-2">
        <button
          onClick={onSkip}
          className="w-full h-12 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium inline-flex items-center justify-center gap-2"
        >
          Continue to payment <ArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => setOpen(true)}
          className="w-full h-10 rounded-xl text-sm text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1.5"
        >
          <Ticket className="w-3.5 h-3.5" /> Have a promo code?
        </button>
      </div>
    );
  }

  const apply = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setBusy(true); setErr(null);
    const res = await validatePromoCode(trimmed, subtotal);
    setBusy(false);
    if ('error' in res) {
      setErr(res.error);
      return;
    }
    onApply(res);
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-3 space-y-2">
      <label className="text-xs text-muted-foreground">Promo code</label>
      <div className="flex gap-2">
        <input
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
          placeholder="WELCOME25"
          className="flex-1 h-10 px-3 rounded-lg border border-border bg-background text-sm font-mono uppercase tracking-wider"
          autoFocus
        />
        <button
          onClick={apply}
          disabled={busy || !code.trim()}
          className="h-10 px-4 rounded-lg bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Apply
        </button>
      </div>
      {err && <div className="text-xs text-destructive">{err}</div>}
      <div className="flex justify-between text-xs">
        <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
          Cancel
        </button>
        <button onClick={onSkip} className="text-muted-foreground hover:text-foreground">
          Continue without code
        </button>
      </div>
    </div>
  );
}

function CardPayment({
  bookingId, onSuccess, onError,
}: { bookingId: string; onSuccess: () => void; onError: (msg: string) => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const pay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements, redirect: 'if_required',
    });
    if (error) {
      onError(error.message || 'Payment failed');
      setBusy(false);
      return;
    }
    if (paymentIntent?.status === 'succeeded') {
      onSuccess();
    } else {
      onError('Payment did not succeed');
      setBusy(false);
    }
    // note: bookingId referenced to satisfy the API contract (parent polls)
    void bookingId;
  };

  return (
    <form onSubmit={pay} className="space-y-3">
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || busy}
        className="w-full h-12 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {busy ? 'Paying…' : 'Pay now'}
      </button>
    </form>
  );
}
